import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, X, Ruler, BedDouble, Bath, Car, MapPin, MessageCircle } from "lucide-react";
import { Reveal, SectionHead, EASE } from "./Reveal";
import { TOUR_ROOMS, TOUR_HOTSPOTS, TOUR_SPECS } from "./tour_data";
import { waLink } from "./data";

export default function TourInteractivo() {
  const [proto, setProto] = useState("b");
  const [roomId, setRoomId] = useState("sala");
  const [idx, setIdx] = useState(0);
  const [modal, setModal] = useState(false);

  const room = TOUR_ROOMS.find((r) => r.id === roomId);
  const hotspots = TOUR_HOTSPOTS[proto];
  const specs = TOUR_SPECS[proto];

  const selectRoom = (id) => {
    setRoomId(id);
    setIdx(0);
  };

  const next = useCallback(() => setIdx((i) => (i + 1) % room.photos.length), [room]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + room.photos.length) % room.photos.length), [room]);

  useEffect(() => {
    if (!modal) return;
    const onKey = (e) => {
      if (e.key === "Escape") setModal(false);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal, next, prev]);

  return (
    <section id="tour" data-testid="tour-section" className="relative py-24 sm:py-32 lg:py-40 bg-[#f4f2ea]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHead
            eyebrow="Tour interactivo"
            light
            title={<>Recorre el departamento<br /><span className="text-[#bfa360] italic">desde aquí</span></>}
          />
          <Reveal delay={0.15} className="shrink-0">
            <div className="inline-flex rounded-full border border-[#1c242f]/10 p-1 bg-white shadow-sm">
              {["a", "b"].map((p) => (
                <button
                  key={p}
                  data-testid={`tour-proto-${p}`}
                  onClick={() => setProto(p)}
                  className={`relative rounded-full px-5 py-2.5 text-[13px] font-medium transition-colors ${
                    proto === p ? "text-[#f4f2ea]" : "text-[#5c6657] hover:text-[#1c242f]"
                  }`}
                >
                  {proto === p && (
                    <motion.span layoutId="tour-proto-pill" className="absolute inset-0 rounded-full bg-[#1c242f]" transition={{ duration: 0.45, ease: EASE }} />
                  )}
                  <span className="relative z-10">Prototipo {p.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Precio englobado */}
        <Reveal delay={0.15}>
          <div data-testid="tour-price-pill" className="mt-8 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-full border border-[#bfa360]/50 bg-white shadow-sm px-6 py-3.5">
            <span className="mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[#5c6657]">28 departamentos de 86 y 101 m²</span>
            <span className="text-[#bfa360]">·</span>
            <span data-testid="tour-price" className="font-display text-xl text-[#93803f]">Prototipo {proto.toUpperCase()} · Desde {specs.price} MXN</span>
          </div>
        </Reveal>

        {/* Quick nav tabs */}
        <Reveal delay={0.1}>
          <div className="mt-10 flex gap-2 overflow-x-auto pb-2 -mx-5 px-5 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="tour-tabs">
            {TOUR_ROOMS.map((r) => (
              <button
                key={r.id}
                data-testid={`tour-tab-${r.id}`}
                onClick={() => selectRoom(r.id)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-[13px] font-medium border transition-colors ${
                  roomId === r.id
                    ? "bg-[#bfa360] border-[#bfa360] text-[#1c242f]"
                    : "border-[#1c242f]/15 text-[#5c6657] hover:border-[#bfa360] hover:text-[#1c242f] bg-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Plano interactivo */}
          <Reveal y={30}>
            <div className="relative rounded-2xl bg-[#213029] p-5 sm:p-8 shadow-xl" data-testid="tour-plan-card">
              <div className="relative inline-block w-full">
                <img
                  src={proto === "a" ? "/images/plan_a.webp" : "/images/plan_b.webp"}
                  alt={`Plano interactivo Prototipo ${proto.toUpperCase()}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto max-h-[560px] object-contain mx-auto"
                />
                {Object.entries(hotspots).map(([id, pos]) => {
                  const r = TOUR_ROOMS.find((x) => x.id === id);
                  const active = roomId === id;
                  return (
                    <button
                      key={id}
                      data-testid={`tour-hotspot-${id}`}
                      onClick={() => selectRoom(id)}
                      aria-label={r.label}
                      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                      className="absolute -translate-x-1/2 -translate-y-1/2 group"
                    >
                      <span className={`absolute inset-0 rounded-full animate-ping ${active ? "bg-[#ddc68c]/50" : "bg-[#bfa360]/30"}`} />
                      <span
                        className={`relative flex items-center justify-center rounded-full border-2 transition-transform duration-300 group-hover:scale-125 ${
                          active
                            ? "w-9 h-9 bg-[#bfa360] border-[#f4f2ea] text-[#1c242f]"
                            : "w-7 h-7 bg-[#1c242f]/90 border-[#bfa360] text-[#ddc68c]"
                        }`}
                      >
                        <MapPin size={active ? 16 : 13} strokeWidth={2.5} />
                      </span>
                      <span className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap mono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full transition-opacity duration-300 ${
                        active ? "opacity-100 bg-[#bfa360] text-[#1c242f]" : "opacity-0 group-hover:opacity-100 bg-[#1c242f] text-[#f4f2ea]"
                      }`}>
                        {r.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-4 text-center mono text-[10px] uppercase tracking-[0.22em] text-[#a8ac9b]">
                Toca un punto del plano para explorar cada espacio
              </p>
            </div>
          </Reveal>

          {/* Panel de detalle */}
          <Reveal y={30} delay={0.1}>
            <AnimatePresence mode="wait">
              <motion.div
                key={roomId + proto}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: EASE }}
              >
                <div className="flex items-baseline justify-between mb-5">
                  <h3 data-testid="tour-room-title" className="font-display text-3xl sm:text-4xl text-[#1c242f]">{room.label}</h3>
                  <span className="mono text-[10px] uppercase tracking-[0.2em] text-[#93803f]">
                    {String(idx + 1).padStart(2, "0")} / {String(room.photos.length).padStart(2, "0")}
                  </span>
                </div>

                {/* Carrusel */}
                <div className="relative rounded-2xl overflow-hidden bg-[#1c242f] group" data-testid="tour-carousel">
                  <button onClick={() => !room.photos[idx].endsWith(".mp4") && setModal(true)} className="block w-full cursor-zoom-in" data-testid="tour-photo-main" aria-label="Ampliar foto">
                    <AnimatePresence mode="wait">
                      {room.photos[idx].endsWith(".mp4") ? (
                        <motion.video
                          key={room.photos[idx]}
                          src={room.photos[idx]}
                          controls
                          playsInline
                          preload="metadata"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.45, ease: EASE }}
                          className="w-full aspect-[4/3] object-cover"
                        />
                      ) : (
                        <motion.img
                          key={room.photos[idx]}
                          src={room.photos[idx]}
                          alt={`${room.label} - foto ${idx + 1}`}
                          initial={{ opacity: 0, scale: 1.03 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.45, ease: EASE }}
                          loading="lazy"
                          decoding="async"
                          className="w-full aspect-[4/3] object-cover"
                        />
                      )}
                    </AnimatePresence>
                  </button>
                  {room.photos.length > 1 && (
                    <>
                      <button data-testid="tour-photo-prev" onClick={prev} aria-label="Anterior"
                        className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-[#1c242f]/70 backdrop-blur text-[#f4f2ea] border border-white/10 hover:bg-[#bfa360] hover:text-[#1c242f] transition-colors">
                        <ChevronLeft size={18} />
                      </button>
                      <button data-testid="tour-photo-next" onClick={next} aria-label="Siguiente"
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-[#1c242f]/70 backdrop-blur text-[#f4f2ea] border border-white/10 hover:bg-[#bfa360] hover:text-[#1c242f] transition-colors">
                        <ChevronRight size={18} />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {room.photos.map((_, i) => (
                          <button
                            key={i}
                            data-testid={`tour-dot-${i}`}
                            onClick={() => setIdx(i)}
                            aria-label={`Foto ${i + 1}`}
                            className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-[#bfa360]" : "w-1.5 bg-white/40"}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Features */}
                <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {room.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#44503f]">
                      <Check size={16} className="mt-0.5 shrink-0 text-[#bfa360]" />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Quick specs */}
                <div data-testid="tour-specs" className="mt-7 grid grid-cols-4 gap-2.5">
                  {[
                    { icon: Ruler, v: specs.area, l: "Área" },
                    { icon: BedDouble, v: specs.rec, l: "Recámaras" },
                    { icon: Bath, v: specs.banos, l: "Baños" },
                    { icon: Car, v: specs.cajones, l: "Cajón" },
                  ].map((s) => (
                    <div key={s.l} className="rounded-xl bg-white border border-[#1c242f]/10 p-3.5 sm:p-4 text-center shadow-sm">
                      <s.icon size={17} className="mx-auto text-[#bfa360] mb-2" />
                      <p className="font-display text-base sm:text-xl text-[#1c242f] leading-none">{s.v}</p>
                      <p className="mono text-[8px] sm:text-[9px] uppercase tracking-[0.15em] text-[#5c6657] mt-1.5">{s.l}</p>
                    </div>
                  ))}
                </div>

                <a
                  data-testid="tour-whatsapp-cta"
                  href={waLink("Hola, acabo de ver el tour interactivo. Quiero agendar una visita guiada.")}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-7 inline-flex items-center gap-3 rounded-full bg-[#1c242f] px-7 py-4 text-sm font-semibold text-[#f4f2ea] hover:bg-[#bfa360] hover:text-[#1c242f] transition-colors"
                >
                  <MessageCircle size={16} />
                  Quiero verlo en persona
                </a>
              </motion.div>
            </AnimatePresence>
          </Reveal>
        </div>
      </div>

      {/* Modal de foto */}
      <AnimatePresence>
        {modal && (
          <motion.div
            data-testid="tour-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#1c242f]/95 backdrop-blur-xl flex items-center justify-center p-4 sm:p-10"
            onClick={() => setModal(false)}
          >
            <button data-testid="tour-lightbox-close" onClick={() => setModal(false)} aria-label="Cerrar"
              className="absolute top-5 right-5 p-3 rounded-full border border-white/15 text-[#f4f2ea] hover:border-[#bfa360] transition-colors z-10">
              <X size={20} />
            </button>
            <button data-testid="tour-lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Anterior"
              className="absolute left-3 sm:left-8 p-3 rounded-full border border-white/15 text-[#f4f2ea] hover:border-[#bfa360] transition-colors z-10">
              <ChevronLeft size={20} />
            </button>
            <button data-testid="tour-lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Siguiente"
              className="absolute right-3 sm:right-8 p-3 rounded-full border border-white/15 text-[#f4f2ea] hover:border-[#bfa360] transition-colors z-10">
              <ChevronRight size={20} />
            </button>
            <motion.figure
              key={room.photos[idx]}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={room.photos[idx]} alt={room.label} className="w-full max-h-[76vh] object-contain rounded-lg" />
              <figcaption className="mt-4 text-center font-display text-xl text-[#f4f2ea]">{room.label}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
